import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, FileArrowUp, WarningCircle } from "@phosphor-icons/react";

/**
 * Public drop page — accessible via /drop/{token}. No auth required. The
 * token itself is the credential; treat the URL as private. Uploads land on
 * the linked Tax Pack's chosen checklist row.
 */
export default function TaxPackDrop() {
    const { token } = useParams();
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const [info, setInfo] = useState(null);
    const [error, setError] = useState(null);
    const [key, setKey] = useState("");
    const [uploading, setUploading] = useState(false);
    const [last, setLast] = useState(null);
    const [dragging, setDragging] = useState(false);

    useEffect(() => {
        axios.get(`${API}/tax-pack/drop/${token}/info`)
            .then(r => { setInfo(r.data); if (r.data.checklist?.[0]) setKey(r.data.checklist[0].key); })
            .catch(() => setError("This link is invalid or has been rotated. Ask the owner for a fresh URL."));
    }, [token, API]);

    const upload = async (file) => {
        if (!file || !key) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("checklist_key", key);
            const { data } = await axios.post(`${API}/tax-pack/drop/${token}`, fd, {
                headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
            });
            setLast({ ok: true, name: data.original_filename, size: data.size });
        } catch (e) {
            setLast({ ok: false, msg: e?.response?.data?.detail || "Upload failed" });
        } finally { setUploading(false); }
    };

    if (error) return (
        <div className="min-h-screen flex items-center justify-center p-8" style={{ background: "var(--bone)" }}>
            <div className="max-w-md text-center">
                <WarningCircle size={40} className="mx-auto text-[var(--terracotta)] mb-4" />
                <div className="font-serif text-2xl mb-2">Link not valid</div>
                <div className="text-sm text-[var(--muted)]">{error}</div>
            </div>
        </div>
    );
    if (!info) return <div className="p-8 text-[var(--muted)]">Loading…</div>;

    const sections = {};
    info.checklist.forEach(c => { (sections[c.section] ||= []).push(c); });

    return (
        <div className="min-h-screen py-10 px-4" style={{ background: "var(--bone)" }}
             data-testid="tax-pack-drop-page">
            <div className="max-w-xl mx-auto">
                <div className="label-eyebrow mb-2">Andrews Financial Planner</div>
                <h1 className="font-serif text-4xl tracking-tight mb-2">
                    Drop a statement <br />→ FY{info.fy} Tax Pack.
                </h1>
                <p className="text-sm text-[var(--muted)] mb-8">
                    Pick which item this file belongs to, then drag it in or tap to select. The file
                    attaches straight to that row and marks it received. You don&rsquo;t need to log in.
                </p>

                <label className="label-eyebrow block mb-2">Which item?</label>
                <select value={key} onChange={e => setKey(e.target.value)}
                        data-testid="drop-key-select"
                        className="w-full border border-[var(--line)] bg-white px-4 py-3 rounded mb-6 text-sm">
                    {Object.entries(sections).map(([sec, rows]) => (
                        <optgroup key={sec} label={sec}>
                            {rows.map(r => (
                                <option key={r.key} value={r.key}>{r.label}</option>
                            ))}
                        </optgroup>
                    ))}
                </select>

                <label htmlFor="drop-file"
                       data-testid="drop-zone"
                       onDragOver={e => { e.preventDefault(); setDragging(true); }}
                       onDragLeave={() => setDragging(false)}
                       onDrop={e => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files?.[0]); }}
                       className={`block border-2 border-dashed rounded p-10 text-center cursor-pointer transition-colors ${
                            dragging ? "border-[var(--eucalyptus)] bg-[var(--eucalyptus)]/10"
                                     : "border-[var(--line)] bg-white hover:border-[var(--eucalyptus)]"}`}>
                    <FileArrowUp size={36} className="mx-auto text-[var(--eucalyptus)] mb-3" />
                    <div className="font-serif text-lg mb-1">
                        {uploading ? "Uploading…" : "Drag a file here or tap to choose"}
                    </div>
                    <div className="text-xs text-[var(--muted)]">
                        PDF · CSV · JPG · PNG · up to 25MB
                    </div>
                    <input id="drop-file" type="file"
                           data-testid="drop-file-input"
                           accept=".pdf,.csv,.jpg,.jpeg,.png,.webp"
                           onChange={e => upload(e.target.files?.[0])}
                           className="hidden" />
                </label>

                {last && (
                    <div className={`mt-6 p-4 rounded flex items-start gap-3 ${
                            last.ok ? "bg-[var(--eucalyptus)]/10" : "bg-[var(--terracotta)]/10"}`}
                         data-testid={last.ok ? "drop-success" : "drop-error"}>
                        {last.ok ? <CheckCircle size={20} weight="fill" className="text-[var(--eucalyptus)] mt-0.5" />
                                 : <WarningCircle size={20} weight="fill" className="text-[var(--terracotta)] mt-0.5" />}
                        <div className="text-sm">
                            {last.ok ? (
                                <>
                                    <div className="font-medium">Received — {last.name}</div>
                                    <div className="text-[var(--muted)] text-xs mt-0.5">
                                        {(last.size / 1024).toFixed(1)} KB · attached to Tax Pack.
                                        Feel free to drop another below.
                                    </div>
                                </>
                            ) : (
                                <div>{last.msg}</div>
                            )}
                        </div>
                    </div>
                )}

                <div className="mt-10 pt-6 border-t border-[var(--line)] text-xs text-[var(--muted)] leading-relaxed">
                    <strong>Private link</strong> — treat this URL like a password.
                    Files land only in the linked Tax Pack. Nothing else on the site is accessible from here.
                </div>
            </div>
        </div>
    );
}
